import { Currency, Language, MeasurementSystem } from "../graphql/generated";

export enum Locale {
  FR = "fr",
  EN = "en",
  EN_US = "en-US",
}

const languageToLocale: { [key in Language]: Locale } = {
  [Language.En]: Locale.EN_US,
  [Language.Fr]: Locale.FR,
};

const localeToMeasurementSystem: { [key in Locale]: MeasurementSystem } = {
  [Locale.FR]: MeasurementSystem.Metric,
  [Locale.EN]: MeasurementSystem.Metric,
  [Locale.EN_US]: MeasurementSystem.Imperial,
};

const measurementSystemToWeightUnit: { [key in MeasurementSystem]: string } = {
  [MeasurementSystem.Metric]: "kg",
  [MeasurementSystem.Imperial]: "lb",
};

const measurementSystemToDistanceUnit: { [key in MeasurementSystem]: string } =
  {
    [MeasurementSystem.Metric]: "m",
    [MeasurementSystem.Imperial]: "ft",
  };

const measurementSystemToMileageUnit: { [key in MeasurementSystem]: string } = {
  [MeasurementSystem.Metric]: "km",
  [MeasurementSystem.Imperial]: "mile",
};

const measurementSystemToTemperatureUnit: {
  [key in MeasurementSystem]: string;
} = {
  [MeasurementSystem.Metric]: "°C",
  [MeasurementSystem.Imperial]: "°F",
};

const measurementSystemToVolumeUnit: { [key in MeasurementSystem]: string } = {
  [MeasurementSystem.Metric]: "L",
  [MeasurementSystem.Imperial]: "gal",
};

export const currencyToUnit: { [key in Currency]: string } = {
  [Currency.Cad]: "CAD",
  [Currency.Usd]: "USD",
  [Currency.Eur]: "EUR",
  [Currency.Zar]: "ZAR",
};

export const currencyToSymbol: { [key in Currency]: string } = {
  [Currency.Cad]: "$",
  [Currency.Usd]: "$",
  [Currency.Eur]: "€",
  [Currency.Zar]: "R",
};

let measurementSystem: MeasurementSystem | null = null;

const LANGUAGE_CACHE_KEY = "LocaleProvider::language";
let language: Language | null =
  (localStorage.getItem(LANGUAGE_CACHE_KEY) as Language) || null;

let currency: Currency | null = null;

class LocaleProvider {
  static getLocale() {
    if (language) {
      return languageToLocale[language];
    }
    const userLocale = navigator.languages
      ? navigator.languages[0]
      : navigator.language;
    return (
      Object.values(Locale).find((locale) => locale === userLocale) ||
      Object.values(Locale).find((locale) => userLocale.includes(locale)) ||
      Locale.EN
    );
  }

  static setLanguage(_language: Language) {
    language = _language;
    localStorage.setItem(LANGUAGE_CACHE_KEY, _language);
  }

  static getLanguage() {
    return language || Language.En;
  }

  static setMeasurementSystem(_measurementSystem: MeasurementSystem) {
    measurementSystem = _measurementSystem;
  }

  static getMeasurementSystem() {
    return (
      measurementSystem ||
      localeToMeasurementSystem[LocaleProvider.getLocale()] ||
      MeasurementSystem.Metric
    );
  }

  static getWeightUnit() {
    return measurementSystemToWeightUnit[LocaleProvider.getMeasurementSystem()];
  }

  static getDistanceUnit() {
    return measurementSystemToDistanceUnit[
      LocaleProvider.getMeasurementSystem()
    ];
  }

  static getMileageUnit() {
    return measurementSystemToMileageUnit[
      LocaleProvider.getMeasurementSystem()
    ];
  }

  static getTemperatureUnit() {
    return measurementSystemToTemperatureUnit[
      LocaleProvider.getMeasurementSystem()
    ];
  }

  static getVolumeUnit() {
    return measurementSystemToVolumeUnit[LocaleProvider.getMeasurementSystem()];
  }

  static getTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  }

  static getCurrencyUnit() {
    return currencyToUnit[currency || Currency.Usd];
  }

  static getCurrencySymbol() {
    return currencyToSymbol[currency || Currency.Usd];
  }

  static setCurrency(_currency: Currency) {
    currency = _currency;
  }
}

export default LocaleProvider;
